import { useMemo } from 'react';
import { trackEvent } from './track';

/* ══════════════════════════════════════════════════════════════════════════
   THE ONE PLACE THAT DECIDES TERMINAL ACCESS
   ─────────────────────────────────────────────────────────────────────────
   SZINTMATRIX (landing repo docs/weboldal-2026-10/plusz/SZINTMATRIX.md):
   level 'pro'   → every tab (PRO subscriber, or an active PRO trial)
   level 'plusz' → Szkenner (top 100), RSI-áttekintés, Funding in full;
                   Stratégiák and ETF as a preview; Derivatívák 22 coins
                   without Put/Call; Hőtérkép top 200
   level 'free'  → Dashboard, Kalkulátor, Trade Tervező, Portfólió, Naptár in
                   full; Derivatívák BTC+ETH only; Hőtérkép top 50; the
                   Plusz and PRO tabs as a blurred preview.
   TAG (affiliate) and OG count as 'free' here — TAG gives the course and
   the newsletter, not the terminal's paid tabs.

   Source: get_user_access().terminal_level (landing repo migrations
   20261005130000, 20261026120000). If the DB does not return it yet, fall
   back to the older fields WITHOUT locking anyone out.
   ══════════════════════════════════════════════════════════════════════════ */

export const PLUSZ_TABS = new Set(['scanner', 'signals', 'funding']);
export const PRO_ONLY_TABS = new Set(['strategies', 'etf']);
export const PRO_TABS = new Set([...PLUSZ_TABS, ...PRO_ONLY_TABS]);
const RANK = { free: 1, plusz: 2, pro: 3 };

/** The lowest level that opens a tab ('free' for the basic tabs). */
export const requiredLevel = (tab) => (PRO_ONLY_TABS.has(tab) ? 'pro' : PLUSZ_TABS.has(tab) ? 'plusz' : 'free');

export function terminalLevel(access) {
  if (!access) return null; // unknown yet (still loading)
  if (['pro', 'plusz', 'free'].includes(access.terminal_level)) return access.terminal_level;
  return access.tier === 'pro' || access.trial_active ? 'pro' : access.tier === 'plusz' ? 'plusz' : 'free';
}

/** The trial button is offered to whoever never had a trial (not to PRO).
    Eligibility (e-mail, earlier claims) is decided by start_trial(); the
    dialog shows the reason if it says no. Old DB without trial_state: no button. */
export const trialCtaFor = (access) => !!access && access.trial_state === 'nincs' && access.tier !== 'pro';

export function useTerminalLevel(access) {
  return useMemo(() => {
    const level = terminalLevel(access);
    return {
      level,
      isPro: level === 'pro',
      isPlusz: level === 'plusz',
      isFree: level === 'free',
      isTrial: !!access?.trial_active && access?.tier !== 'pro',
      isPaying: access?.tier === 'pro' || access?.tier === 'plusz',
      isTag: access?.tier === 'affiliate',
      canOpen: (tab) => !!level && RANK[level] >= RANK[requiredLevel(tab)],
      trialCta: trialCtaFor(access),
    };
  }, [access]);
}

/* Paywall measurement. trackEvent already swallows errors (console.warn
   only), so a failed insert never blocks the UI. */
export function trackPaywall(name, props, user) {
  try { trackEvent(name, props || {}, user); } catch (e) { console.warn('[paywall]', name, e); }
}
